import type { Models } from "appwrite";

type CollectionBase<T> = Models.Row & T;

export const QuestionType = {
	Single: "Single",
	Multiple: "Multiple",
	Text: "Text",
	Info: "Info",
} as const;
export type QuestionType = keyof typeof QuestionType;

export const AssetPosition = {
	BeforeQuestion: "beforeQuestion",
	AfterQuestion: "afterQuestion",
} as const;
export type AssetPosition = (typeof AssetPosition)[keyof typeof AssetPosition];

export const QuizType = {
	Survey: "Survey",
	GameShow: "GameShow",
	Exam: "Exam",
} as const;
export type QuizType = keyof typeof QuizType;

export const ExamIdentityMode = {
	Guest: "Guest",
	Account: "Account",
} as const;
export type ExamIdentityMode = keyof typeof ExamIdentityMode;

export const ShareMode = {
	Public: "public",
	Members: "members",
	Invited: "invited",
} as const;
export type ShareMode = (typeof ShareMode)[keyof typeof ShareMode];

export type Project = CollectionBase<{
	userId: string[];
	name: string;
	done: boolean;
	description: string | null;
	quizType: QuizType;
	examPassingScore?: number;
	examIdentityMode?: ExamIdentityMode;
	examMaxAttempts?: number;
	shareMode?: ShareMode;
	shareTeamId?: string | null;
	questionnaires?: Questionnaire[];
}>;

export type InputProject = Omit<Project, "questionnaires"> & {
	questionnaires?: string[];
};

export const ProjectRole = {
	Viewer: "viewer",
	Editor: "editor",
	Admin: "admin",
} as const;
export type ProjectRole = (typeof ProjectRole)[keyof typeof ProjectRole];

export type ProjectMember = CollectionBase<{
	projectId: string;
	ownerId: string;
	userId: string;
	email: string;
	name: string | null;
	role: ProjectRole;
}>;

export type Questionnaire = CollectionBase<{
	userId: string;
	name: string;
	done: boolean;
	previewImage: string | null;
	questions?: Question[];
	project?: Project;
}>;

export type InputQuestionnaire = Omit<
	Questionnaire,
	"questions" | "project"
> & {
	questions?: string[];
	project?: string;
};

export type Question = CollectionBase<{
	userId: string;
	question: string;
	questionType: QuestionType;
	options: string[];
	correctOption: string[];
	asset: string | null;
	assetPosition?: AssetPosition;
	questionnaire?: Questionnaire;
	explanation?: string | null;
}>;

export type InputQuestion = Omit<Question, "questionnaire"> & {
	questionnaire?: string;
};

export type LiveParticipationMode = "qr" | "screen";
export type LiveSelectionMode = "picker" | "random";
export type LiveQuestionPlanItem = {
	questionId: string;
	questionnaireId: string;
	points: number;
	question: Question;
};
export type LiveQuestionResult = CollectionBase<{
	sessionId: string;
	questionId: string;
	points: number;
	answersJson: string;
	correctTeamIds: string[];
}>;

export type LiveSession = CollectionBase<{
	projectId: string;
	hostId: string;
	status: "lobby" | "playing" | "finished";
	currentQuestionIndex: number;
	correctAnswersVisible?: boolean;
	participationMode?: LiveParticipationMode;
	selectionMode?: LiveSelectionMode;
	currentQuestionId?: string | null;
	questionPlanJson?: string | null;
}>;

export type LiveTeam = CollectionBase<{
	sessionId: string;
	name: string;
	emoji: string | null;
	color: string | null;
}>;

export type LiveAnswer = CollectionBase<{
	sessionId: string;
	teamId: string;
	questionId: string;
	answers: string[];
}>;

export type QuizParticipation = CollectionBase<{
	projectId: string;
	quizType: Exclude<QuizType, "Exam">;
	ownerId: string;
	sessionId: string;
}>;

export type ExamInvitation = CollectionBase<{
	projectId: string;
	ownerId: string;
	tokenHash: string;
	recipientName: string | null;
	recipientEmail?: string | null;
	recipientUserId?: string | null;
	identityMode: ExamIdentityMode;
	maxAttempts: number;
	usedAttempts: number;
	linkType?: "personal" | "group";
}>;

export type ExamAttempt = CollectionBase<{
	projectId: string;
	invitationId: string;
	ownerId: string;
	participantName: string;
	participantUserId: string | null;
	status: "started" | "finished";
	score: number | null;
	maxScore: number | null;
	percentage: number | null;
	passed: boolean | null;
	submittedAt?: string | null;
	answerDetailsJson?: string | null;
}>;

export type CertificateTemplate = CollectionBase<{
	ownerId: string;
	name: string;
	orientation: "portrait" | "landscape";
	designJson: string;
	backgroundFileId: string | null;
}>;

export type SigningCredential = CollectionBase<{
	ownerId: string;
	subjectName: string;
	issuerName: string;
	fingerprint: string;
	validFrom: string;
	validTo: string;
	encryptedFileId: string;
	status: "active" | "expired" | "removed";
}>;

export type Certificate = CollectionBase<{
	attemptId: string;
	projectId: string;
	ownerId: string;
	participantUserId: string | null;
	participantName: string;
	templateId: string;
	version: number;
	certificateNumber: string;
	status: "generating" | "issued" | "revoked" | "failed";
	fileId: string | null;
	documentHash: string | null;
	signerName: string;
	signerFingerprint: string;
	issuedAt: string | null;
	revokedAt: string | null;
}>;
